/* eslint-disable no-console */
import mfaEmailLogo from "@assets/images/mfa-email-logo.svg";
import mfaInfoLogo from "@assets/images/mfa-info.svg";
import mfaMobileLogo from "@assets/images/mfa-mobile-logo.svg";
import mfaScanQrLogo from "@assets/images/mfa-scan-qr-logo.svg";
import smLogo from "@assets/images/smo-blue.svg";
import { Box, Card, CssBaseline, Paper, Typography } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import useNotifier from "@saleor/hooks/useNotifier";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useMfaPhoneSendCode, useMfaSendEmailCode } from "../../mutations";
import theme from "../../theme";
import MfaCustomAuthCard, { AuthMethod } from "../MfaCustomCard/mfaCustomCard";
import useStyles from "./styles";

const MfaAuthPage: React.FC = () => {
  const classes = useStyles({});
  const navigate = useNavigate();
  const location = useLocation();
  const mfaLocalStorage = localStorage.getItem("mfaLocalStorage");
  const { userEmail } =
    location.state || (mfaLocalStorage ? JSON.parse(mfaLocalStorage) : {});

  const notify = useNotifier();

  const [sendEmailCode] = useMfaSendEmailCode({});
  const [sendPhoneCode] = useMfaPhoneSendCode({});
  const [loading, setLoading] = useState(false);
  const [loadingPhone, setLoadingPhone] = useState(false);
  const [isPhoneClick, setIsPhoneClick] = useState(
    localStorage.getItem("isPhoneClick") === "true"
  );
  const handleEmailCardClick = async () => {
    setLoading(true);
    try {
      const response = await sendEmailCode({
        variables: { email: userEmail }
      });

      console.log("Full Response:", response);

      const mfaResponse = response.data?.mfaSendCode;
      let mfaMessage = mfaResponse?.message;

      const EmailResendCooldown = ResendCooldown => {
        const now = Math.floor(Date.now() / 1000);
        const lockoutUntil = now + ResendCooldown;
        Cookies.set("emailResendCooldown", lockoutUntil.toString());
      };

      EmailResendCooldown(mfaResponse?.data.lockoutPeriod);

      if (typeof mfaMessage === "string" && mfaMessage.startsWith("['")) {
        try {
          mfaMessage = JSON.parse(mfaMessage.replace(/'/g, '"'));
        } catch (error) {
          console.error("Error parsing MFA message:", error);
        }
      }

      console.log("Parsed MFA Message:", mfaMessage);

      if (!mfaResponse.success && mfaResponse.message === "MFA704") {
        notify({
          status: "error",
          text: `Too many failed attempts`
        });

        setLoading(false);
        return;
      }

      if (mfaResponse?.success) {
        Cookies.remove("PhoneLockedOut");
        Cookies.remove("EmailLockedOut");
        Cookies.set("isEmailResendCooldown", "true");
        console.log("Auth page " + userEmail + mfaResponse.data.createdAt!);

        const cooldownSeconds = mfaResponse.data.resendCooldown || 300;
        Cookies.set("mfaCodeSent", "true", {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaCreatedAt", mfaResponse.data.createdAt!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaUserEmail", userEmail!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
          expires: cooldownSeconds / 86400
        });

        navigate("/mfa-email-input-page/", {
          state: {
            createdAt: mfaResponse.data.createdAt!,
            email: userEmail!,
            isEmailSendCode: true,
            isPhoneSendCode: false
          }
        });
      } else {
        console.error("MFA Email Code Sending Failed:", mfaMessage);

        if (mfaMessage === "MFA704" && Cookies.get("mfaCodeSent")) {
          notify({
            status: "warning",
            text:
              "Check your email for the code. Redirecting to the Authentication Code page in 3 seconds..."
          });

          setTimeout(() => {
            navigate("/mfa-email-input-page/", {
              state: {
                createdAt: Cookies.get("mfaCreatedAt"),
                email: Cookies.get("mfaUserEmail"),
                isEmailSendCode: true,
                isPhoneSendCode: false
              }
            });
          }, 3000);
        } else {
          notify({
            status: "error",
            text: "MFA Email Code Sending Failed"
          });
        }
      }
    } catch (err) {
      console.error("Error sending email code:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneCardClick = async () => {
    Cookies.set("isPhoneClicked", "true");
    setLoadingPhone(true);
    localStorage.setItem("isPhoneClick", "true");
    try {
      const response = await sendPhoneCode({
        variables: { email: userEmail }
      });

      console.log("Full Response:", response);

      const mfaPhoneSendCode = response.data?.mfaPhoneSendCode;
      const mfaMessage = mfaPhoneSendCode?.message;

      let parsedMessage: string[] = [];

      if (typeof mfaMessage === "string") {
        try {
          parsedMessage = JSON.parse(mfaMessage.replace(/'/g, '"'));
        } catch (error) {
          console.error("Error parsing MFA message:", error);
        }
      }

      const PhoneResendCooldown = (resendCooldown: number | null) => {
        if (resendCooldown) {
          const now = Math.floor(Date.now() / 1000);
          const lockoutUntil = now + resendCooldown;
          Cookies.set("phoneResendCooldown", lockoutUntil.toString());
        }
      };

      PhoneResendCooldown(mfaPhoneSendCode?.data.lockoutPeriod ?? null);
      Cookies.set("isPhoneResendCooldown", "true");

      if (!mfaPhoneSendCode.success && parsedMessage.includes("MFA704")) {
        notify({
          status: "error",
          text: "Too many failed attempts."
        });
        setLoading(false);
        return;
      }

      if (mfaPhoneSendCode?.success) {
        Cookies.remove("PhoneLockedOut");
        Cookies.remove("EmailLockedOut");
        const cooldownSeconds = mfaPhoneSendCode.data.resendCooldown || 300;
        Cookies.set("mfaCodeSent", "true", {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaCreatedAt", mfaPhoneSendCode.data.createdAt!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaUserEmail", userEmail!, {
          expires: cooldownSeconds / 86400
        });
        Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
          expires: cooldownSeconds / 86400
        });

        navigate("/mfa-phone-input-page/", {
          state: {
            createdAt: mfaPhoneSendCode.data.createdAt!,
            email: userEmail!,
            isEmailSendCode: false,
            isPhoneSendCode: true
          }
        });
      } else {
        console.error("MFA Phone Code Sending Failed:", parsedMessage);
        setLoadingPhone(false);
        if (parsedMessage.includes("MFA704") && Cookies.get("mfaCodeSent")) {
          notify({
            status: "warning",
            text:
              "Check your email for the code. Redirecting to the Authentication Code page in 3 seconds..."
          });

          setTimeout(() => {
            navigate("/mfa-phone-input-page/", {
              state: {
                createdAt: Cookies.get("mfaCreatedAt") || "",
                email: Cookies.get("mfaUserEmail") || "",
                isEmailSendCode: false,
                isPhoneSendCode: true
              }
            });
          }, 3000);
        } else if (parsedMessage.includes("MFA706")) {
          notify({
            status: "error",
            text: "MFA Failed sending SMS."
          });
        } else if (parsedMessage.includes("MFA404")) {
          notify({
            status: "error",
            text: "MFA Phone number is invalid."
          });
        } else {
          notify({
            status: "error",
            text: "MFA Phone Code Sending Failed"
          });
        }
      }
    } catch (err) {
      console.error("Error sending phone code:", err);
    } finally {
      setLoading(false);
    }
  };

  const hasMaxAttemptsEmail = Cookies.get("hasMaxAttemptsEmail");
  const isEmailResendCooldown = Cookies.get("isEmailResendCooldown");

  const now = Math.floor(Date.now() / 1000);
  const lockoutUntil = parseInt(Cookies.get("lockoutUntil") || "0", 10);
  const emailResendCooldown = parseInt(
    Cookies.get("emailResendCooldown") || "0",
    10
  );
  const phoneResendCooldown = parseInt(
    Cookies.get("phoneResendCooldown") || "0",
    10
  );
  const countdown = Math.max(0, lockoutUntil - now);
  const countdownEmail = Math.max(0, emailResendCooldown - now);
  const countdownPhone = Math.max(0, phoneResendCooldown - now);
  const [timeLeft, setTimeLeft] = useState(countdown);
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === "isPhoneClick") {
        setIsPhoneClick(event.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (hasMaxAttemptsEmail === "true") {
      localStorage.removeItem("isPhoneClick");
      setIsPhoneClick(false);
    }
    if (timeLeft === 0) {
      localStorage.setItem("isPhoneClick", "true");
      setIsPhoneClick(true);
    }
  }, [hasMaxAttemptsEmail, timeLeft]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className={classes.centerContainer}>
        <img className={classes.logo} src={smLogo} alt="SM Logo" />
        <Card className={classes.card}>
          <Typography variant="h5" component="h5" className={classes.title}>
            Choose Authentication Method
          </Typography>
          <Typography className={classes.subtitle}>
            Your account verification will be based on your selected method.
          </Typography>
          <div className={classes.buttonContainer}>
            {hasMaxAttemptsEmail && (
              <MfaCustomAuthCard
                onClick={handlePhoneCardClick}
                imageSrc={mfaMobileLogo}
                countdown={countdown || countdownPhone}
                authMethod={AuthMethod.SendPhoneCode}
                loading={loadingPhone}
                tag={{ label: "SendPhoneCode", state: "active" }}
                isDisabled={
                  timeLeft === 0 ||
                  Cookies.get("isPhoneClicked") === "true" ||
                  isPhoneClick
                }
              />
            )}

            <MfaCustomAuthCard
              onClick={handleEmailCardClick}
              imageSrc={mfaEmailLogo}
              countdown={countdown || countdownEmail}
              authMethod={AuthMethod.SendEmailCode}
              loading={loading}
              hasTimer={
                isEmailResendCooldown === "true" ||
                hasMaxAttemptsEmail === "true"
                  ? true
                  : false
              }
              tag={{ label: "SendEmailCode", state: "active" }}
              isDisabled={loadingPhone}
            />
            <MfaCustomAuthCard
              onClick={null}
              imageSrc={mfaScanQrLogo}
              tag={{ label: "Coming Soon", state: "disabled" }}
              hasTimer={false}
              authMethod={AuthMethod.SendQRCode}
              isDisabled={true}
            />
          </div>
          <Paper className={classes.infoCard} elevation={0}>
            <img
              className={classes.infoLogo}
              src={mfaInfoLogo}
              alt="MFA info"
            />
            <Typography style={{ fontSize: 12 }} align="left">
              <b>Need help?</b> Reach out to{" "}
              <b>smmallsonline.acquisition@smsupermalls.com</b>
            </Typography>
          </Paper>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default MfaAuthPage;
